import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ChevronRight, ChevronLeft, Check, Building2, GraduationCap, Search,
  BookOpen, Globe, MapPin, Phone, Mail, Layers, CheckCircle2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  university: 'Universite Publique', university_private: 'Universite Privee',
  higher_institute: 'Institut Superieur', isp: 'Institut Superieur Pedagogique (ISP)',
  technical_institute: 'Institut Technique', medical_institute: 'Institut Medical',
  specialized_school: 'Ecole Specialisee', other: 'Autre'
};

const TYPE_ICONS = {
  university: GraduationCap, university_private: GraduationCap,
  higher_institute: Building2, isp: BookOpen,
  technical_institute: Building2, medical_institute: Building2,
};

export const InstitutionWizardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState(null); // 'catalog' or 'manual'
  const [loading, setLoading] = useState(false);

  // Catalog state
  const [catalogList, setCatalogList] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogFilter, setCatalogFilter] = useState('all');
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [catalogDetail, setCatalogDetail] = useState(null);
  const [selectedStructures, setSelectedStructures] = useState([]);
  const [coursesPreview, setCoursesPreview] = useState({});

  // Form state
  const [form, setForm] = useState({
    name: '', code: '', type: 'university', country: 'RDC',
    province: '', city: '', address: '', phone: '', email: '', website: '',
  });
  const [provinces, setProvinces] = useState([]);
  const [modules, setModules] = useState([]);
  const [enabledModules, setEnabledModules] = useState([]);

  // Load catalog and provinces
  useEffect(() => {
    const load = async () => {
      try {
        const [catRes, provRes] = await Promise.all([
          axios.get(`${API}/core/catalog/institutions`),
          axios.get(`${API}/core/catalog/provinces`),
        ]);
        setCatalogList(catRes.data);
        setProvinces(provRes.data);
      } catch (e) { console.error(e); }
    };
    load();
  }, []);

  // Load modules for manual flow
  useEffect(() => {
    const loadModules = async () => {
      try {
        const { data } = await axios.get(`${API}/core/modules`);
        setModules(data);
        setEnabledModules(data.filter(m => m.is_core).map(m => m.key));
      } catch (e) { console.error(e); }
    };
    loadModules();
  }, []);

  // Filter catalog
  const filteredCatalog = useMemo(() => {
    let list = catalogList;
    if (catalogSearch) {
      const q = catalogSearch.toLowerCase();
      list = list.filter(i => i.name.toLowerCase().includes(q) || i.code.toLowerCase().includes(q));
    }
    if (catalogFilter !== 'all') {
      list = list.filter(i => i.type === catalogFilter);
    }
    return list;
  }, [catalogList, catalogSearch, catalogFilter]);

  // Load catalog detail
  const selectCatalogItem = async (item) => {
    setSelectedCatalog(item);
    try {
      const { data } = await axios.get(`${API}/core/catalog/institutions/${item.code}`);
      setCatalogDetail(data);
      setSelectedStructures(data.structures.map(s => s.name));
      setForm(prev => ({
        ...prev, name: data.name, code: data.code, type: data.type,
        city: data.city || '', province: data.province || '', website: data.website || '',
      }));
      // Load courses preview for each department
      const preview = {};
      for (const struct of data.structures) {
        for (const dept of (struct.departments || [])) {
          if (dept.code) {
            try {
              const cr = await axios.get(`${API}/core/catalog/courses/${dept.code}`);
              if (cr.data.total > 0) preview[dept.code] = cr.data.total;
            } catch (e) { /* no courses for this dept */ }
          }
        }
      }
      setCoursesPreview(preview);
    } catch (e) { toast.error('Erreur de chargement'); }
  };

  const toggleStructure = (name) => {
    setSelectedStructures(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const toggleAllStructures = () => {
    if (!catalogDetail) return;
    const all = catalogDetail.structures.map(s => s.name);
    setSelectedStructures(prev => prev.length === all.length ? [] : all);
  };

  const toggleModule = (key) => {
    const mod = modules.find(m => m.key === key);
    if (mod?.is_core) return;
    setEnabledModules(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Submit
  const createFromCatalog = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/core/institutions/from-catalog`, {
        catalog_code: selectedCatalog.code,
        selected_structures: selectedStructures,
        custom_name: form.name !== selectedCatalog?.name ? form.name : undefined,
        custom_email: form.email,
        custom_phone: form.phone,
      });
      toast.success(data.message);
      navigate('/organization');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la creation');
    } finally { setLoading(false); }
  };

  const createManual = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/core/institutions`, {
        ...form,
        enabled_modules: enabledModules,
      });
      toast.success(`"${form.name}" cree avec succes`);
      navigate('/organization');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  // ============ STEP 0: Mode Selection ============
  const renderModeSelection = () => (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="mode-selection">
      <div className="text-center mb-6">
        <h2 className="text-lg font-semibold">Comment souhaitez-vous creer l'institution ?</h2>
        <p className="text-sm text-gray-500 mt-1">Choisissez une methode de creation</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card
          className={`cursor-pointer border-2 transition-all hover:shadow-md ${mode === 'catalog' ? 'border-blue-600 bg-blue-50/50' : 'border-gray-200'}`}
          onClick={() => setMode('catalog')}
          data-testid="mode-catalog"
        >
          <CardContent className="pt-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mb-3">
              <BookOpen className="w-7 h-7 text-blue-600" />
            </div>
            <h3 className="font-semibold text-base">Depuis le Catalogue RDC</h3>
            <p className="text-sm text-gray-500 mt-2 leading-relaxed">
              Selectionnez une institution existante en RDC. Les facultes, sections et departements sont deja pre-remplis.
            </p>
            <Badge className="mt-3" variant="secondary">Recommande</Badge>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer border-2 transition-all hover:shadow-md ${mode === 'manual' ? 'border-blue-600 bg-blue-50/50' : 'border-gray-200'}`}
          onClick={() => setMode('manual')}
          data-testid="mode-manual"
        >
          <CardContent className="pt-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center mb-3">
              <Building2 className="w-7 h-7 text-gray-600" />
            </div>
            <h3 className="font-semibold text-base">Creation Manuelle</h3>
            <p className="text-sm text-gray-500 mt-2 leading-relaxed">
              Creez une nouvelle institution manuellement. Remplissez les informations et les structures vous-meme.
            </p>
            <Badge className="mt-3" variant="outline">Personnalise</Badge>
          </CardContent>
        </Card>
      </div>
      <div className="flex justify-end">
        <Button onClick={() => setStep(1)} disabled={!mode} data-testid="next-step-btn">
          Continuer <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  // ============ CATALOG FLOW ============
  const renderCatalogSearch = () => (
    <div className="space-y-4" data-testid="catalog-search">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Rechercher une institution (UNIKIN, ISP Gombe, INBTP...)"
            value={catalogSearch}
            onChange={e => setCatalogSearch(e.target.value)}
            data-testid="catalog-search-input"
          />
        </div>
        <Select value={catalogFilter} onValueChange={setCatalogFilter}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            {Object.entries(TYPE_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <ScrollArea className="h-[420px]">
        <div className="space-y-2">
          {filteredCatalog.length === 0 ? (
            <p className="text-center py-8 text-sm text-gray-400">Aucune institution trouvee</p>
          ) : (
            filteredCatalog.map(item => {
              const Icon = TYPE_ICONS[item.type] || Building2;
              const isSelected = selectedCatalog?.code === item.code;
              return (
                <div
                  key={item.code}
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border ${
                    isSelected ? 'border-blue-600 bg-blue-50' : 'border-gray-100 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => selectCatalogItem(item)}
                  data-testid={`catalog-item-${item.code}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{item.name}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <MapPin className="w-3 h-3" /> {item.city}, {item.province}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <Badge variant="outline" className="text-[10px]">{item.code}</Badge>
                    <p className="text-[10px] text-gray-400 mt-1">{item.structures_count} structures</p>
                  </div>
                  {isSelected && <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />}
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => { setStep(0); setSelectedCatalog(null); setCatalogDetail(null); }}>
          <ChevronLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <Button onClick={() => setStep(2)} disabled={!selectedCatalog} data-testid="next-step-btn">
          Continuer <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  const renderCatalogStructures = () => (
    <div className="space-y-4" data-testid="catalog-structures">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">{catalogDetail?.name}</h2>
          <p className="text-sm text-gray-500">Selectionnez les facultes/sections a inclure</p>
        </div>
        <Button variant="outline" size="sm" onClick={toggleAllStructures}>
          {selectedStructures.length === catalogDetail?.structures?.length ? 'Tout deselectionner' : 'Tout selectionner'}
        </Button>
      </div>

      <ScrollArea className="h-[400px]">
        <div className="space-y-3">
          {catalogDetail?.structures?.map((struct) => {
            const isSelected = selectedStructures.includes(struct.name);
            return (
              <Card
                key={struct.name}
                className={`cursor-pointer border transition-all ${isSelected ? 'border-blue-500 bg-blue-50/30' : 'border-gray-200 hover:border-gray-300'}`}
                onClick={() => toggleStructure(struct.name)}
              >
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                      isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{struct.name}</span>
                        <Badge variant="secondary" className="text-[10px]">{struct.code}</Badge>
                      </div>
                      {struct.departments && struct.departments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {struct.departments.map(d => {
                            const courseCount = coursesPreview[d.code];
                            return (
                              <Badge key={d.code || d.name} variant="outline" className="text-[10px] font-normal">
                                {d.name}{courseCount ? ` (${courseCount} cours)` : ''}
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </ScrollArea>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep(1)}>
          <ChevronLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <Button onClick={() => setStep(3)} disabled={selectedStructures.length === 0} data-testid="next-step-btn">
          Continuer ({selectedStructures.length} selectionne{selectedStructures.length > 1 ? 's' : ''}) <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  const renderCatalogContact = () => (
    <div className="space-y-4" data-testid="catalog-contact">
      <h2 className="text-base font-semibold">Informations de contact</h2>
      <p className="text-sm text-gray-500">Optionnel — vous pouvez completer plus tard</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>Nom de l'institution</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} /></div>
        <div><Label>Code</Label><Input value={form.code} disabled className="bg-gray-50" /></div>
        <div><Label>Email</Label><Input value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="contact@institution.ac.cd" /></div>
        <div><Label>Telephone</Label><Input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} placeholder="+243..." /></div>
        <div><Label>Province</Label><Input value={form.province} disabled className="bg-gray-50" /></div>
        <div><Label>Ville</Label><Input value={form.city} disabled className="bg-gray-50" /></div>
      </div>

      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="pt-4">
          <h3 className="text-sm font-medium mb-2">Resume</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div><p className="text-xl font-bold text-blue-600">{selectedStructures.length}</p><p className="text-xs text-gray-500">Structures</p></div>
            <div><p className="text-xl font-bold text-green-600">
              {catalogDetail?.structures?.filter(s => selectedStructures.includes(s.name)).reduce((sum, s) => sum + (s.departments?.length || 0), 0)}
            </p><p className="text-xs text-gray-500">Departements</p></div>
            <div><p className="text-xl font-bold text-teal-600">
              {catalogDetail?.structures?.filter(s => selectedStructures.includes(s.name)).reduce((sum, s) =>
                sum + (s.departments || []).reduce((ds, d) => ds + (coursesPreview[d.code] || 0), 0), 0)}
            </p><p className="text-xs text-gray-500">Cours pre-remplis</p></div>
            <div><p className="text-xl font-bold text-purple-600">{TYPE_LABELS[form.type]?.split(' ')[0] || '-'}</p><p className="text-xs text-gray-500">Type</p></div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep(2)}>
          <ChevronLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <Button onClick={createFromCatalog} disabled={loading} data-testid="create-institution-btn">
          {loading ? 'Creation...' : 'Creer l\'institution'}
        </Button>
      </div>
    </div>
  );

  // ============ MANUAL FLOW ============
  const renderManualInfo = () => (
    <div className="space-y-4" data-testid="manual-info">
      <h2 className="text-base font-semibold">Informations Generales</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div><Label>Nom de l'institution *</Label>
          <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Nom complet" /></div>
        <div><Label>Code / Sigle *</Label>
          <Input value={form.code} onChange={e => setForm(p => ({...p, code: e.target.value}))} placeholder="Ex: UNIKIN" /></div>
        <div><Label>Type</Label>
          <Select value={form.type} onValueChange={v => setForm(p => ({...p, type: v}))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Province</Label>
          <Select value={form.province} onValueChange={v => setForm(p => ({...p, province: v}))}>
            <SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger>
            <SelectContent>{provinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Ville</Label><Input value={form.city} onChange={e => setForm(p => ({...p, city: e.target.value}))} /></div>
        <div><Label>Adresse</Label><Input value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))} /></div>
        <div><Label>Telephone</Label><Input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} placeholder="+243..." /></div>
        <div><Label>Email</Label><Input value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} /></div>
        <div className="md:col-span-2"><Label>Site Web</Label><Input value={form.website} onChange={e => setForm(p => ({...p, website: e.target.value}))} /></div>
      </div>
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep(0)}>
          <ChevronLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <Button onClick={() => setStep(2)} disabled={!form.name || !form.code} data-testid="next-step-btn">
          Continuer <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  const renderManualModules = () => (
    <div className="space-y-4" data-testid="manual-modules">
      <h2 className="text-base font-semibold">Modules a activer</h2>
      <p className="text-sm text-gray-500">Les modules "Core" sont obligatoires</p>
      <ScrollArea className="h-[350px]">
        <div className="space-y-2">
          {modules.map(m => (
            <div key={m.key} className="flex items-center justify-between p-3 rounded-lg border">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{m.name_fr}</span>
                  {m.is_core && <Badge variant="default" className="text-[10px]">Core</Badge>}
                </div>
              </div>
              <Switch
                checked={enabledModules.includes(m.key)}
                onCheckedChange={() => toggleModule(m.key)}
                disabled={m.is_core}
              />
            </div>
          ))}
        </div>
      </ScrollArea>
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep(1)}>
          <ChevronLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <Button onClick={() => setStep(3)} data-testid="next-step-btn">
          Continuer <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  const renderManualConfirm = () => (
    <div className="space-y-4" data-testid="manual-confirm">
      <h2 className="text-base font-semibold">Confirmation</h2>
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-gray-500">Nom:</span> <strong>{form.name}</strong></div>
            <div><span className="text-gray-500">Code:</span> <strong>{form.code}</strong></div>
            <div><span className="text-gray-500">Type:</span> <strong>{TYPE_LABELS[form.type]}</strong></div>
            <div><span className="text-gray-500">Province:</span> <strong>{form.province || '-'}</strong></div>
            <div><span className="text-gray-500">Ville:</span> <strong>{form.city || '-'}</strong></div>
            <div><span className="text-gray-500">Modules:</span> <strong>{enabledModules.length}</strong></div>
          </div>
        </CardContent>
      </Card>
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep(2)}>
          <ChevronLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <Button onClick={createManual} disabled={loading} data-testid="create-institution-btn">
          {loading ? 'Creation...' : 'Creer l\'institution'}
        </Button>
      </div>
    </div>
  );

  // ============ STEPS ============
  const getSteps = () => {
    if (mode === 'catalog') return ['Methode', 'Catalogue RDC', 'Structures', 'Finalisation'];
    if (mode === 'manual') return ['Methode', 'Informations', 'Modules', 'Confirmation'];
    return ['Methode', '...', '...', '...'];
  };
  const steps = getSteps();

  const renderContent = () => {
    if (step === 0) return renderModeSelection();
    if (mode === 'catalog') {
      if (step === 1) return renderCatalogSearch();
      if (step === 2) return renderCatalogStructures();
      if (step === 3) return renderCatalogContact();
    } else {
      if (step === 1) return renderManualInfo();
      if (step === 2) return renderManualModules();
      if (step === 3) return renderManualConfirm();
    }
    return null;
  };

  if (!['super_admin', 'co_admin'].includes(user?.role)) {
    return <div className="p-6 text-center text-gray-500">Acces non autorise</div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="institution-wizard">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                i < step ? 'bg-green-600 text-white' :
                i === step ? 'bg-blue-600 text-white' :
                'bg-gray-200 text-gray-400'
              }`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`ml-2 text-xs hidden sm:inline ${i === step ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>{s}</span>
              {i < steps.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300 mx-2" />}
            </div>
          ))}
        </div>
      </div>

      {renderContent()}
    </div>
  );
};
